/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import * as yup from 'yup'; // for everything
import { isEmpty, set } from 'lodash';

// Types
import { TestContext } from 'yup';
import { Regex, Validator } from '../../../../types/ProductFactory';
import i18n, { handleResponseLanguage, useLocale } from '../../../../i18n';
import { validateInputCall } from '../../../common/src/legacy/services/apiRouterService';
import { isValidPhoneNumber } from 'react-phone-number-input';
import {
  FlowQuestionRules,
  FlowsQuestion,
  FlowsQuestionErrorMessages,
} from '../../../flows/src/types/FlowsTypes';
import { useMemo } from 'react';
import { subDays } from 'date-fns';

interface YupNodeObject {
  [key: string]: (string | number)[] | undefined;
}

const useYup = (
  questions: FlowsQuestion[],
): yup.AnyObjectSchema | undefined => {
  /**
   * This function converts a yup array to an actual yup node that can be
   * used for a schema object. Reduce is used because each yup function has
   * to be executed on the result of the previous yup function.
   * @param yupArray Array with yup keys and its values
   */

  // Yup has bad TS support: https://github.com/jquense/yup/issues/1190

  const { userLang } = useLocale();
  const createYupNode = (yupArray: YupNodeObject[]): any => {
    if (!yupArray) return [];
    return yupArray.reduce((prevResult: any, obj) => {
      const [key, val] = Object.entries(obj)[0];
      if (val && typeof prevResult?.[key] === 'function')
        return prevResult?.[key](...val);
      return null;
    }, yup);
  };

  /**
   * Used to make an API call for validating fields
   * @param val Current value of the field
   * @param resolve Callback to resolve promise
   */
  const validate = async (
    val: string | number,
    inputType: string,
    resolve: (arg0: string | boolean) => void,
    validator?: Validator,
  ) => {
    if (!validator && inputType === 'TELEPHONE_NUMBER') {
      const isPossible = isValidPhoneNumber(val as string);

      return resolve(
        !isPossible &&
          (i18n.t(
            'Invalid telephone number Please use the international format',
          ) as string),
      );
    }

    if (!validator) return;
    const [resp, status] = await validateInputCall('POST', validator.url, {
      [validator.body]: val,
    });

    if (inputType === 'TELEPHONE_NUMBER') {
      const isPossible = isValidPhoneNumber(val as string);

      return resolve(
        (status !== 200 || !isPossible) &&
          (i18n.t(
            'Invalid telephone number Please use the international format',
          ) as string),
      );
    }

    // Exception for cbe
    if (val === '          ') return resolve(false);

    if (status !== 200) {
      if (validator.url.split('/').pop() === 'iban')
        return resolve(i18n.t('INVALID-FORMAT') || resp?.deail);
      if (typeof resp?.detail === 'object')
        return resolve(handleResponseLanguage(resp?.msg) || resp?.msg);
      return resolve(handleResponseLanguage(resp?.detail) || resp?.detail);
    }
    return resolve(false);
  };

  /**
   * Parses the value to the correct format for yup validation
   * @param val The value that might be parsed depending on input_type and type
   */
  const parseValue = (
    rules: FlowQuestionRules,
    val: string | number | true | Regex | Date,
  ) => {
    if (rules.input_type?.toLowerCase() === 'date' && typeof val === 'string') {
      return subDays(new Date(val), 1);
    }
    return val;
  };

  /**
   * This function maps a rulekey to a yup function name. This way the yup function
   * can be invoked so that the yup validation schema can be build up.
   *
   * Ex: Rulekey => mandatory translates to the Yupkey => Required
   *     Required() is the equivalent yup function
   * @param ruleKey Rulekey that comes from backend
   * @param val Value for the rulekey received from backend
   */
  const matchRuleKeyWithYup = (
    rules: FlowQuestionRules,
    ruleKey: string,
    val: string | boolean | number | Regex | Date,
    error_messages?: FlowsQuestionErrorMessages,
  ) => {
    const optionList = [];
    if (val == null || val === false) return null;
    let errorMsg;

    val = parseValue(rules, val);

    // Procedure:
    // 1. Match rulekey
    // 2. IF value needed => Push rulekey value
    // 3. IF erorr message => Push error message
    // 4. Return object

    switch (ruleKey) {
      case 'data_type':
        errorMsg = handleResponseLanguage(
          error_messages?.data_type || 'Invalid',
        );
        if (errorMsg) optionList.push(errorMsg);
        if (typeof val === 'string' && val?.toLowerCase() === 'number') {
          errorMsg = 'You must specify a number';
        }
        return { typeError: optionList };
      case 'mandatory':
        if (val == null) return null;
        errorMsg = handleResponseLanguage(error_messages?.mandatory);
        if (errorMsg) optionList.push(errorMsg);
        return { required: optionList };
      case 'gt':
        optionList.push(val);
        errorMsg = handleResponseLanguage(error_messages?.gt);
        if (errorMsg) optionList.push(errorMsg);
        return { moreThan: optionList };
      case 'lt':
        optionList.push(val);
        errorMsg = handleResponseLanguage(error_messages?.lt);
        if (errorMsg) optionList.push(errorMsg);
        return { lessThan: optionList };
      case 'ge':
        optionList.push(val);
        errorMsg = handleResponseLanguage(error_messages?.ge);
        if (errorMsg) optionList.push(errorMsg);
        return { min: optionList };
      case 'le':
        optionList.push(val);
        errorMsg = handleResponseLanguage(error_messages?.le);
        if (errorMsg) optionList.push(errorMsg);
        return { max: optionList };
      case 'min_length':
        optionList.push(val);
        errorMsg = handleResponseLanguage(error_messages?.min_length);
        if (errorMsg) optionList.push(errorMsg);
        return { min: optionList };
      case 'max_length':
        optionList.push(val);
        errorMsg = handleResponseLanguage(error_messages?.max_length);
        if (errorMsg) optionList.push(errorMsg);
        return { max: optionList };
      case 'regex':
        optionList.push(new RegExp('(^$|' + (val as Regex).js + ')'));
        errorMsg = handleResponseLanguage(error_messages?.regex);
        if (errorMsg) optionList.push(errorMsg);
        return { matches: optionList };
      default:
        return null;
    }
  };

  const wrapInYupObjects = (yupObject: any) => {
    // Once yup schema is found it will stop the recursion
    if (Object.getPrototypeOf(yupObject).constructor.name !== 'Object') {
      return yupObject;
    }

    yupObject = yup.object().shape(
      Object.assign(
        {},
        ...Object.entries(yupObject).map(([key, value]) => ({
          [key]: wrapInYupObjects(value),
        })),
      ),
    );
    return yupObject;
  };

  /**
   * Create a yup schema that can be used for validation
   */
  const createYupSchema = (
    // id: string,
    // rules: Rules,
    // validator?: Validator,
    // error_messages?: ErrorMessages
    question: FlowsQuestion[],
    // Yup has bad typing so using any for this case
  ): yup.AnyObjectSchema => {
    const yupObject: { [x: string]: any } = {};

    question.forEach(({ id, rules, validator, error_messages }) => {
      const yupNodeObject: YupNodeObject[] = [];
      // Return if question contains no rules or validator
      if (isEmpty(rules) && !validator) return yup.object();

      // Create yup nodes for each property of a rules object
      Object.entries(rules).forEach(([key, val]) => {
        if (key === 'data_type') {
          if (rules.input_type === 'DATE') {
            yupNodeObject.splice(0, 0, {
              [rules.input_type.toLowerCase()]: [],
            });
          } else {
            yupNodeObject.splice(0, 0, { [val.toLowerCase()]: [] });
          }
        }
        const yupRulesArray = matchRuleKeyWithYup(
          rules,
          key,
          val,
          error_messages,
        );

        if (yupRulesArray != null) yupNodeObject.push(yupRulesArray as any);

        // add minimum to mandatory arrays

        if (
          rules.data_type === 'ARRAY' &&
          key === 'mandatory' &&
          val === true
        ) {
          /** Make sure empty arrays are falsy */
          const optionList = [];
          const errorMsg = handleResponseLanguage(error_messages?.mandatory);
          optionList.push(1);
          if (errorMsg) optionList.push(errorMsg);
          yupNodeObject.push({ min: optionList });
        }
      });

      // Create validators that call API routes for validation
      // TODO: Remove 'TELEPHONE_NUMBER' check once Kenzo has added all validators
      if (validator?.body || rules?.input_type === 'TELEPHONE_NUMBER') {
        yupNodeObject.push({
          test: [
            validator?.body,
            handleResponseLanguage(error_messages?.regex) || 'Invalid',
            (async (val: string | number, testContext: TestContext) => {
              if (val) {
                const error = await new Promise((resolve) =>
                  validate(val, rules?.input_type, resolve, validator),
                );
                if (error)
                  return testContext.createError({ message: error as string });
                return true;
              }
              return true;
            }) as any,
          ],
        });
      }

      // Allow null values
      yupNodeObject.push({ nullable: [] });

      set(yupObject, id, createYupNode(yupNodeObject));
    });

    const schema = wrapInYupObjects(yupObject);
    return schema;
  };

  const schema = useMemo(
    () => createYupSchema(questions),
    [questions, userLang],
  );
  return schema;
};

export default useYup;
